import { DebtLabel, PriceLabel, RenderSubscriptionStatus } from '@/components/features/shared/utils'
import { AlertWarning } from '@/components/ui/alertWarning'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { SelectableRow } from '@/components/ui/selectable-row'
import { BillingFrequency } from '@/domain/offerings/offering'
import { SubscriptionData, isBillingPeriodPending } from '@/domain/subscriptions/subscriptions'
import { capitalizeString } from '@/utils/strings'
import { format } from 'date-fns'
import { es } from 'date-fns/locale'
import { AlertCircle, Check, CheckSquare, X } from 'lucide-react'
import { useState } from 'react'

interface DeleteBillingPeriodModalProps {
  isOpen: boolean
  onClose: () => void
  subscriptions: SubscriptionData[]
  onDelete: (billingPeriods: { subscriptionId: string; billingPeriodId: string }[]) => void
  isPending: boolean
}

export const DeleteBillingPeriodModal = ({
  isOpen,
  onClose,
  subscriptions,
  onDelete,
  isPending,
}: DeleteBillingPeriodModalProps) => {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  const pendingSubscriptions = subscriptions.filter(isBillingPeriodPending)

  const totalSelected = selectedIds.size

  const getSubscriptionPeriod = (periodStartDate: Date, billingFrequency = pendingSubscriptions?.[0]?.billingFrequency) => {
    return billingFrequency === BillingFrequency.ANNUAL ?
      format(periodStartDate, 'yyyy')
      : capitalizeString(format(periodStartDate, 'MMMM', { locale: es })) + '/' + format(new Date(periodStartDate), 'yyyy', { locale: es })
  }

  const toggleSelection = (billingPeriodId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(billingPeriodId)) {
        next.delete(billingPeriodId)
      } else {
        next.add(billingPeriodId)
      }
      return next
    })
  }

  const selectAll = () => {
    setSelectedIds(prev => {
      if (prev.size === pendingSubscriptions.length) {
        return new Set()
      }
      return new Set(pendingSubscriptions.map(s => s.billingPeriodId))
    })
  }

  const handleConfirmDelete = () => {
    if (selectedIds.size === 0) return
    const selected = pendingSubscriptions
      .filter(s => selectedIds.has(s.billingPeriodId))
      .map(s => ({ subscriptionId: s.subscriptionId, billingPeriodId: s.billingPeriodId }))
    onDelete(selected)
    setSelectedIds(new Set())
  }

  const handleCancel = () => {
    setSelectedIds(new Set())
    onClose()
  }

  if (pendingSubscriptions.length === 0) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminar Período</DialogTitle>
            <div className="flex items-center gap-2 text-muted-foreground text-sm py-4">
              <AlertCircle className="h-4 w-4" />
              No hay períodos pendientes para eliminar
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cerrar</Button>
            </DialogFooter>
          </DialogHeader>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Eliminar Períodos</DialogTitle>
          <DialogDescription>
            Elige los períodos pendientes o adeudados del contrato a eliminar.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 my-4">
          <div className="flex items-center justify-between pb-2 border-b">
            <span className="text-sm text-muted-foreground font-medium">
              {pendingSubscriptions.length} período{pendingSubscriptions.length !== 1 ? 's' : ''} pendiente{pendingSubscriptions.length !== 1 ? 's' : ''}
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={selectAll}
              disabled={isPending}
            >
              <CheckSquare className="h-3 w-3 mr-1" />
              Seleccionar todos
            </Button>
          </div>

          <div className="space-y-2 overflow-y-auto pr-2 flex-grow">
            {pendingSubscriptions.map(sub => {
              const isSelected = selectedIds.has(sub.billingPeriodId)
              return (
                <SelectableRow
                  key={sub.billingPeriodId}
                  checked={isSelected}
                  disabled={isPending}
                  onCheckChange={() => toggleSelection(sub.billingPeriodId)}
                >
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={isSelected}
                      disabled={isPending}
                    />
                    <div className="flex flex-col items-start gap-0.5">
                      <span className="font-medium">{sub.offeringName}</span>
                      <span className="text-xs text-muted-foreground">
                        {getSubscriptionPeriod(sub.periodStartDate, sub.billingFrequency)}
                      </span>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <RenderSubscriptionStatus subscriptionStatus={sub.periodStatus} />
                    {sub.outstandingAmount > 0 && (
                      <DebtLabel amount={sub.outstandingAmount} />
                    )}
                    {sub.outstandingAmount === 0 && (
                      <PriceLabel price={sub.totalAmount} />
                    )}
                  </div>
                </SelectableRow>
              )
            })}
          </div>
        </div>

        <AlertWarning>
          Esta acción eliminará los períodos seleccionados permanentemente. No se puede deshacer.
        </AlertWarning>

        <DialogFooter className="mt-4">
          <div className='flex justify-between w-full'>
            <Button variant="outline" onClick={handleCancel} disabled={isPending}>
              <X className="h-3.5 w-3.5 mr-1.5" />
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={selectedIds.size === 0 || isPending}
            >
              <Check className="h-3.5 w-3.5 mr-1.5" />
              Eliminar {totalSelected > 0 ? `(${totalSelected})` : ''}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
